import useSWR from 'swr';

import { useIsChain } from '@utils/useIsChain';

import { getChannels, listChannelDefinitions } from './lib/channels';
import { useShop } from './ShopProvider';
import { getChannel } from './lib/channels';

export {
	getAvailableChannels,
	getChannels,
	listChannelDefinitions,
	registerInterest,
	createChannel,
	createMetaSystemUser,
	getChannel,
	getOrderOptions,
	getOrderOptionsForOrganisation,
	updateChannel,
	enableChannel,
	disableChannel,
	getDeliveryCategories
} from './lib/channels';

/**
 * @typedef {import('@nearst/channels-data/model/common/mapper').ChannelDomainObject} Channel
 *
 * /

/**
 * Create a new channel for a shop
 *
 * @param {string} shopId Shop ID -
 * @param {Channel["type"]} type The type of channel to create
 * @param {object} body The body to be sent to create the channel
 * @returns {Promise<Channel>}
 */

/**
 * Returns a list of channels that are enabled for the current shop or organisation.
 * @returns {Channel[]|undefined} Array of channel objects that are enabled, or undefined if none are enabled.
 */
export function useEnabledChannels() {
	const { shop, data: shops } = useShop();
	const { data: channels } = useSWR([shop.id, 'channels'], () => getChannels(shop.id ? [shop] : shops));
	return channels?.filter((channel) => channel.enabledShops.length > 0);
}

/**
 * Returns delivery channels (Uber Eats, Deliveroo, Just Eat) that are enabled for the current shop or organization.
 * @returns {Channel[]|undefined} Array of delivery channel objects that are enabled, or undefined if none are enabled.
 */
export function useDeliveryChannels() {
	const isChain = useIsChain();
	const { shop, data: shops } = useShop();
	const channels = useEnabledChannels();

	const findLastMileChannel = (shopId, channel) => {
		return channel.enabledShops.includes(shopId);
	};
	const deliveryChannels = channels
		?.filter((channel) => ['uber-eats', 'deliveroo', 'just-eat'].includes(channel.id))
		.filter((channel) =>
			isChain ? shops.some((shop) => findLastMileChannel(shop.id, channel)) : findLastMileChannel(shop.id, channel)
		);
	return deliveryChannels;
}

/**
 * @typedef {import('swr').SWRResponse} SWRResponse
 * @typedef {import('swr').SWRConfiguration} SWRConfiguration
 * @typedef {SWRResponse} ChannelResponse
 * @property {Channel} ChannelResponse.data
 */

/**
 * Retrieves channels.
 * @param {Channel["type"]} type - The type of channel to retrieve
 * @param {string} [shopId] - The shop id to fetch the channel for. If undefined, fetch channels for whole org
 * @param {SWRConfiguration} [swrOptions] - Optional extra swr config
 * @returns {ChannelResponse}
 */
export const useChannel = (
	type,
	shopId,
	swrOptions = {
		suspense: true
	}
) => {
	const key = ['channel', shopId, type];
	return useSWR(key, () => getChannel(type, shopId), swrOptions);
};

/**
 * @typedef {SWRResponse} ChannelDefinitionResponse
 * @property {Channel} ChannelDefinitionResponse.data
 */

/**
 * Retrieves channel definitions.
 * @param {string} [channelId] - The id of the channel to fetch definitions for
 * @param {SWRConfiguration} [swrOptions] - Optional extra swr definitions
 * @returns {ChannelDefinitionResponse}
 */
export const useChannelDefinitions = (
	channelId,
	swrOptions = {
		suspense: true
	}
) => {
	const key = `channels-config`;
	return useSWR(key, () => listChannelDefinitions(channelId), swrOptions);
};
